import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ApiDocs = () => {
  const [activeSection, setActiveSection] = useState('auth');
  const [activeLanguage, setActiveLanguage] = useState('curl');
  const [copied, setCopied] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const contentRef = useRef(null);

  const sections = [
    { id: 'auth', label: 'Authentication', icon: '🔐', color: '#f59e0b' },
    { id: 'kundli', label: 'Kundali Data', icon: '🪐', color: '#8b5cf6' },
    { id: 'horoscope', label: 'Daily Horoscope', icon: '⭐', color: '#3b82f6' },
    { id: 'numerology', label: 'Numerology', icon: '🔢', color: '#10b981' },
    { id: 'tarot', label: 'Tarot Readings', icon: '🃏', color: '#ec4899' },
    { id: 'fortune', label: 'Fortune Cookie', icon: '🥠', color: '#f97316' },
    { id: 'compatibility', label: 'Love Compatibility', icon: '💕', color: '#ef4444' },
    { id: 'zodiac-gift', label: 'Zodiac Gifts', icon: '🎁', color: '#06b6d4' },
    { id: 'ai-chat', label: 'AI Consultation', icon: '🤖', color: '#6366f1' },
  ];

  const languages = [
    { id: 'curl', label: 'cURL', icon: '⌘' },
    { id: 'javascript', label: 'JavaScript', icon: 'JS' },
    { id: 'python', label: 'Python', icon: '🐍' },
  ];

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeSection]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const apiBase = (process.env.REACT_APP_API_URL || "http://localhost:5001").replace(/\/api$/, "");

  const currentSection = sections.find(s => s.id === activeSection);

  const getCodeSnippet = (section, lang) => {
    const snippets = {
      auth: {
        curl: `curl -X POST ${apiBase}/api/coustomer/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "your_email@example.com",
    "password": "your_password"
  }'`,
        javascript: `const response = await fetch('${apiBase}/api/coustomer/login', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    email: 'your_email@example.com',
    password: 'your_password'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/api/coustomer/login"
payload = {
    "email": "your_email@example.com",
    "password": "your_password"
}

response = requests.post(url, json=payload)
print(response.json())`
      },
      kundli: {
        curl: `curl -X POST ${apiBase}/kundli-data \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "section": "all",
    "dateOfBirth": "1995-12-05",
    "birthTime": "14:30",
    "wb_latitude": "28.6139",
    "wb_longitude": "77.2090",
    "wb_timezone": "5.5"
  }'`,
        javascript: `const response = await fetch('${apiBase}/kundli-data', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    section: 'all',
    dateOfBirth: '1995-12-05',
    birthTime: '14:30',
    wb_latitude: '28.6139',
    wb_longitude: '77.2090',
    wb_timezone: '5.5'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/kundli-data"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "section": "all",
    "dateOfBirth": "1995-12-05",
    "birthTime": "14:30",
    "wb_latitude": "28.6139",
    "wb_longitude": "77.2090",
    "wb_timezone": "5.5"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      horoscope: {
        curl: `curl -X POST ${apiBase}/divine-horoscope-data \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "wb_sign": "Aries",
    "wb_type": "daily",
    "wb_tab": "today"
  }'`,
        javascript: `const response = await fetch('${apiBase}/divine-horoscope-data', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    wb_sign: 'Aries',
    wb_type: 'daily',
    wb_tab: 'today'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/divine-horoscope-data"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "wb_sign": "Aries",
    "wb_type": "daily",
    "wb_tab": "today"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      numerology: {
        curl: `curl -X POST ${apiBase}/numerology-data \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "firstName": "John",
    "lastName": "Doe",
    "dateOfBirth": "1990-01-15"
  }'`,
        javascript: `const response = await fetch('${apiBase}/numerology-data', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    firstName: 'John',
    lastName: 'Doe',
    dateOfBirth: '1990-01-15'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/numerology-data"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "firstName": "John",
    "lastName": "Doe",
    "dateOfBirth": "1990-01-15"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      tarot: {
        curl: `curl -X POST ${apiBase}/tarot-data \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "wb_api_name": "love"
  }'`,
        javascript: `// Endpoints: /tarot-data (1 card), /tarot-2, /tarot-3
const response = await fetch('${apiBase}/tarot-data', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    wb_api_name: 'love'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

# Endpoints: /tarot-data (1 card), /tarot-2, /tarot-3
url = "${apiBase}/tarot-data"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "wb_api_name": "love"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      fortune: {
        curl: `curl -X POST ${apiBase}/fortune-cookie-detail \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE"`,
        javascript: `const response = await fetch('${apiBase}/fortune-cookie-detail', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  }
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/fortune-cookie-detail"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}

response = requests.post(url, headers=headers)
print(response.json())`
      },
      compatibility: {
        curl: `curl -X POST ${apiBase}/love-compatibility-detail \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "sign_1": "Aries",
    "sign_2": "Leo"
  }'`,
        javascript: `const response = await fetch('${apiBase}/love-compatibility-detail', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    sign_1: 'Aries',
    sign_2: 'Leo'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/love-compatibility-detail"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "sign_1": "Aries",
    "sign_2": "Leo"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      'zodiac-gift': {
        curl: `curl -X POST ${apiBase}/zodiac-gift-guru-data \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "sign": "Taurus"
  }'`,
        javascript: `const response = await fetch('${apiBase}/zodiac-gift-guru-data', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    sign: 'Taurus'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/zodiac-gift-guru-data"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "sign": "Taurus"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      },
      'ai-chat': {
        curl: `curl -X POST ${apiBase}/astrology-chat-detail \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: YOUR_PURCHASE_CODE" \\
  -d '{
    "question": "What does my career look like?",
    "birthDate": "1993-08-20",
    "birthTime": "09:45",
    "birthPlace": "Mumbai"
  }'`,
        javascript: `const response = await fetch('${apiBase}/astrology-chat-detail', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_PURCHASE_CODE'
  },
  body: JSON.stringify({
    question: 'What does my career look like?',
    birthDate: '1993-08-20',
    birthTime: '09:45',
    birthPlace: 'Mumbai'
  })
});

const data = await response.json();
console.log(data);`,
        python: `import requests

url = "${apiBase}/astrology-chat-detail"
headers = {
    "x-api-key": "YOUR_PURCHASE_CODE"
}
payload = {
    "question": "What does my career look like?",
    "birthDate": "1993-08-20",
    "birthTime": "09:45",
    "birthPlace": "Mumbai"
}

response = requests.post(url, json=payload, headers=headers)
print(response.json())`
      }
    };
    return snippets[section]?.[lang] || '';
  };

  const getResponseExample = (section) => {
    const examples = {
      auth: {
        success: true,
        message: "Signed in successfully",
        User: { id: 5, name: "John Doe", email: "john@example.com" }
      },
      kundli: {
        success: 1,
        planets: [
          { name: "Sun", rashi: "Scorpio", degree: "18° 24'", house: 8 },
          { name: "Moon", rashi: "Taurus", degree: "05° 12'", house: 2 }
        ],
        houses: { ascendant: "Aries", ascendant_degree: "12° 40'" }
      },
      horoscope: {
        success: 1,
        sign: "Aries",
        date: "2026-06-10",
        prediction: "Today brings great opportunities for career growth..."
      },
      numerology: {
        success: 1,
        res_data: {
          life_path: 7, destiny: 5, soul_urge: 3,
          description: "Your life path number 7 signifies a quest for knowledge..."
        }
      },
      tarot: {
        success: 1,
        cards: [{
          name: "The Fool", type: "Major Arcana",
          description: "New beginnings, optimism, trust in life.",
          direction: "Upright", image: "uploads/tarot/the_fool.png"
        }]
      },
      fortune: {
        success: 1,
        fortune: "An unexpected relationship changes your life...",
        lucky_number: 18, lucky_time: "4:00 PM"
      },
      compatibility: {
        success: 1,
        percentage: "85%",
        summary: "Aries and Leo share a dynamic, passionate bond..."
      },
      'zodiac-gift': {
        success: 1,
        res_data: {
          sign: "Taurus",
          gifts: ["High quality items", "Comfortable home decor", "Spa voucher"],
          tip: "Focus on sensory elements and luxurious comfort."
        }
      },
      'ai-chat': {
        success: 1,
        response: "Based on your birth date, Saturn is currently aspecting your 10th house..."
      }
    };
    return JSON.stringify(examples[section], null, 2);
  };

  const getHeaders = (section) => {
    if (section === 'auth') {
      return [
        { name: 'Content-Type', value: 'application/json', description: 'Required. JSON body format.' }
      ];
    }
    return [
      { name: 'Content-Type', value: 'application/json', description: 'Required. JSON body format.' },
      { name: 'x-api-key', value: 'YOUR_PURCHASE_CODE', description: 'Required. Your subscription API key from Account Dashboard.' }
    ];
  };

  const getBodyParams = (section) => {
    const params = {
      auth: [
        { name: 'email', type: 'string', required: true, description: 'Registered account email address.' },
        { name: 'password', type: 'string', required: true, description: 'Account password.' }
      ],
      kundli: [
        { name: 'section', type: 'string', required: false, description: '"planets", "charts", "readings", or "all".' },
        { name: 'dateOfBirth', type: 'string', required: true, description: 'YYYY-MM-DD format (e.g. 1990-11-25).' },
        { name: 'birthTime', type: 'string', required: true, description: 'HH:MM in 24hr format (e.g. 18:30).' },
        { name: 'wb_latitude', type: 'string', required: true, description: 'Birth place latitude (e.g. 28.6139).' },
        { name: 'wb_longitude', type: 'string', required: true, description: 'Birth place longitude (e.g. 77.2090).' },
        { name: 'wb_timezone', type: 'string', required: true, description: 'GMT timezone offset (e.g. 5.5).' }
      ],
      horoscope: [
        { name: 'wb_sign', type: 'string', required: true, description: 'Zodiac sign: "Aries", "Taurus", etc.' },
        { name: 'wb_type', type: 'string', required: true, description: '"daily", "weekly", "monthly", or "yearly".' },
        { name: 'wb_tab', type: 'string', required: true, description: '"yesterday", "today", or "tomorrow".' }
      ],
      numerology: [
        { name: 'firstName', type: 'string', required: true, description: 'First name of the person.' },
        { name: 'lastName', type: 'string', required: true, description: 'Last name of the person.' },
        { name: 'dateOfBirth', type: 'string', required: true, description: 'YYYY-MM-DD format.' }
      ],
      tarot: [
        { name: 'wb_api_name', type: 'string', required: true, description: '"love", "career", "general", or "finance".' }
      ],
      fortune: [],
      compatibility: [
        { name: 'sign_1', type: 'string', required: true, description: 'First partner zodiac sign.' },
        { name: 'sign_2', type: 'string', required: true, description: 'Second partner zodiac sign.' }
      ],
      'zodiac-gift': [
        { name: 'sign', type: 'string', required: true, description: 'Target zodiac sign name.' }
      ],
      'ai-chat': [
        { name: 'question', type: 'string', required: true, description: 'Your astrology question.' },
        { name: 'birthDate', type: 'string', required: false, description: 'YYYY-MM-DD format.' },
        { name: 'birthTime', type: 'string', required: false, description: 'HH:MM format.' },
        { name: 'birthPlace', type: 'string', required: false, description: 'City/location of birth.' }
      ]
    };
    return params[section] || [];
  };

  const getMethodAndUrl = (section) => {
    const routes = {
      auth: { method: 'POST', url: '/api/coustomer/login' },
      kundli: { method: 'POST', url: '/kundli-data' },
      horoscope: { method: 'POST', url: '/divine-horoscope-data' },
      numerology: { method: 'POST', url: '/numerology-data' },
      tarot: { method: 'POST', url: '/tarot-data' },
      fortune: { method: 'POST', url: '/fortune-cookie-detail' },
      compatibility: { method: 'POST', url: '/love-compatibility-detail' },
      'zodiac-gift': { method: 'POST', url: '/zodiac-gift-guru-data' },
      'ai-chat': { method: 'POST', url: '/astrology-chat-detail' }
    };
    return routes[section] || { method: 'GET', url: '' };
  };

  const getSectionDescription = (id) => {
    const descs = {
      auth: 'Authenticate your client session using email and password credentials. Returns user profile data upon successful login.',
      kundli: 'Generate detailed Kundali (birth chart) data including planetary positions, house details, and astrological readings based on birth coordinates.',
      horoscope: 'Retrieve daily, weekly, monthly, or yearly horoscope predictions for any of the twelve zodiac signs.',
      numerology: 'Calculate Life Path, Destiny, and Soul Urge numbers using numerological algorithms based on name and birth date.',
      tarot: 'Draw 1, 2, or 3 tarot cards for customized readings. Supports focus areas like love, career, general, and finance.',
      fortune: 'Generate a random fortune cookie message along with lucky number and lucky time. No request body required.',
      compatibility: 'Calculate romantic compatibility percentage and detailed analysis between two zodiac signs.',
      'zodiac-gift': 'Get personalized gift recommendations tailored to a specific zodiac sign\'s personality traits.',
      'ai-chat': 'Consult our advanced AI astrology engine with personalized questions using optional birth details for deeper insights.'
    };
    return descs[id] || '';
  };

  const sidebarContent = (
    <>
      <div className="p-5 pb-3">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div>
            <h2 className="text-[14px] font-extrabold text-slate-800 leading-tight">API Reference</h2>
            <p className="text-[10px] text-slate-400 font-semibold">v1.0 — REST API</p>
          </div>
        </div>
      </div>

      <div className="px-3 mb-3">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-3.5">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Base URL</p>
          <code className="text-[11px] font-mono font-bold text-slate-700 break-all">{apiBase}</code>
        </div>
      </div>

      <div className="px-3 mb-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-2 mb-2">Endpoints</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
        {sections.map((sec) => (
          <button
            key={sec.id}
            onClick={() => { setActiveSection(sec.id); setMobileSidebarOpen(false); }}
            className={`w-full text-left px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-all duration-200 flex items-center gap-2.5 group relative ${
              activeSection === sec.id
                ? 'bg-white text-slate-800 shadow-sm shadow-slate-200/80'
                : 'text-slate-500 hover:bg-white/60 hover:text-slate-700'
            }`}
          >
            {activeSection === sec.id && (
              <motion.div
                layoutId="activeIndicator"
                className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full"
                style={{ backgroundColor: sec.color }}
                transition={{ type: 'spring', stiffness: 350, damping: 30 }}
              />
            )}
            <span className="text-[15px] w-6 text-center flex-shrink-0">{sec.icon}</span>
            <span className="truncate">{sec.label}</span>
            {activeSection === sec.id && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: sec.color }}
              />
            )}
          </button>
        ))}
      </nav>
    </>
  );

  return (
    <div className="apidocs-wrapper" style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <style>{`
        .apidocs-wrapper {
          --sidebar-width: 280px;
        }
        .apidocs-topbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 54px;
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid #e2e8f0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 24px;
        }
        .apidocs-sidebar {
          position: fixed;
          top: 54px;
          left: 0;
          bottom: 0;
          width: var(--sidebar-width);
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
          border-right: 1px solid #e2e8f0;
          z-index: 40;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .apidocs-main {
          margin-left: var(--sidebar-width);
          padding-top: 54px;
          min-height: 100vh;
        }
        .code-block {
          background: #0f172a;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.1);
        }
        .code-block-header {
          background: linear-gradient(180deg, #1e293b 0%, #1a2436 100%);
          border-bottom: 1px solid #334155;
          padding: 10px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .param-table {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          background: white;
        }
        .param-table thead {
          background: #f8fafc;
        }
        .param-table th {
          padding: 10px 16px;
          text-align: left;
          font-size: 10px;
          font-weight: 800;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          border-bottom: 1px solid #e2e8f0;
        }
        .param-table td {
          padding: 12px 16px;
          font-size: 13px;
          border-bottom: 1px solid #f1f5f9;
          vertical-align: top;
        }
        .param-table tr:last-child td {
          border-bottom: none;
        }
        .endpoint-badge {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #0f172a, #1e293b);
          border: 1px solid #334155;
          border-radius: 10px;
          padding: 10px 16px;
          width: 100%;
        }
        .method-tag {
          background: linear-gradient(135deg, #4f46e5, #6366f1);
          color: white;
          font-size: 11px;
          font-weight: 900;
          padding: 4px 10px;
          border-radius: 6px;
          letter-spacing: 0.05em;
        }
        .copy-btn {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          color: #94a3b8;
          font-size: 11px;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .copy-btn:hover {
          background: rgba(255,255,255,0.12);
          color: #e2e8f0;
        }
        .lang-tab {
          font-size: 11px;
          font-weight: 700;
          padding: 5px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          background: transparent;
          color: #64748b;
        }
        .lang-tab.active {
          background: rgba(99, 102, 241, 0.2);
          color: #a5b4fc;
        }
        .lang-tab:hover:not(.active) {
          color: #cbd5e1;
        }
        .section-hero {
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f5f3ff 100%);
          border-bottom: 1px solid #e2e8f0;
          padding: 32px 0;
        }
        .required-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ef4444;
          margin-right: 4px;
          vertical-align: middle;
        }
        .optional-dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d1d5db;
          margin-right: 4px;
          vertical-align: middle;
        }
        .mobile-menu-btn {
          display: none;
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 50;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: linear-gradient(135deg, #4f46e5, #6366f1);
          color: white;
          border: none;
          cursor: pointer;
          box-shadow: 0 10px 30px rgba(79, 70, 229, 0.4);
          align-items: center;
          justify-content: center;
        }
        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 45;
          backdrop-filter: blur(4px);
        }
        .mobile-sidebar {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 300px;
          background: white;
          z-index: 55;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 10px 0 40px rgba(0,0,0,0.15);
        }
        @media (max-width: 768px) {
          .apidocs-sidebar { display: none !important; }
          .apidocs-main { margin-left: 0 !important; }
          .mobile-menu-btn { display: flex !important; }
          .mobile-overlay.open { display: block !important; }
          .mobile-sidebar.open { display: flex !important; }
        }
      `}</style>


      {/* Top Bar */}
      <div className="apidocs-topbar">
        <a href="/" className="flex items-center gap-2 text-slate-800 font-extrabold text-[15px] tracking-tight hover:text-indigo-600 transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          AstroSphere API
        </a>
        <div className="flex items-center gap-4">
          <a href="/" className="text-[12px] font-bold text-slate-500 hover:text-slate-800 transition-colors">Home</a>
          <a href="/#module-price" className="text-[12px] font-bold text-slate-500 hover:text-slate-800 transition-colors">Pricing</a>
          <span className="text-[12px] font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">API Docs</span>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="apidocs-sidebar hidden md:flex">
        {sidebarContent}
      </aside>

      {/* Mobile Menu Button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setMobileSidebarOpen(true)}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Mobile Overlay */}
      <div className={`mobile-overlay ${mobileSidebarOpen ? 'open' : ''}`} onClick={() => setMobileSidebarOpen(false)} />

      {/* Mobile Sidebar */}
      <div className={`mobile-sidebar ${mobileSidebarOpen ? 'open' : ''}`}>
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <span className="font-bold text-slate-800">Navigation</span>
          <button onClick={() => setMobileSidebarOpen(false)} className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M8.41425 7.00022L13.7073 1.70719C14.0983 1.31719 14.0983 0.68325 13.7073 0.29325C13.3163 -0.09775 12.6842 -0.09775 12.2933 0.29325L7.00025 5.58622L1.70725 0.29325C1.31625 -0.09775 0.68425 -0.09775 0.29325 0.29325C-0.09775 0.68325 -0.09775 1.31719 0.29325 1.70719L5.58625 7.00022L0.29325 12.2933C-0.09775 12.6833 -0.09775 13.3172 0.29325 13.7072C0.48825 13.9022 0.744251 14.0002 1.00025 14.0002C1.25625 14.0002 1.51225 13.9022 1.70725 13.7072L7.00025 8.41422L12.2933 13.7072C12.4882 13.9022 12.7443 14.0002 13.0003 14.0002C13.2563 14.0002 13.5123 13.9022 13.7073 13.7072C14.0983 13.3172 14.0983 12.6833 13.7073 12.2933L8.41425 7.00022Z" fill="#64748b" />
            </svg>
          </button>
        </div>
        {sidebarContent}
      </div>

      {/* Main Content */}
      <main className="apidocs-main" ref={contentRef}>

        {/* Section Hero */}
        <div className="section-hero">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[20px]"
                  style={{ background: `${currentSection?.color}15`, border: `1px solid ${currentSection?.color}30` }}
                >
                  {currentSection?.icon}
                </div>
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: currentSection?.color }}>
                    API Endpoint
                  </span>
                  <h1 className="text-2xl font-extrabold text-slate-800 tracking-tight leading-tight">
                    {currentSection?.label}
                  </h1>
                </div>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed max-w-2xl ml-[52px]">
                {getSectionDescription(activeSection)}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="grid grid-cols-1 lg:grid-cols-5 gap-8"
            >
              {/* Left: Documentation (3 cols) */}
              <div className="lg:col-span-3 space-y-6">

                {/* Endpoint URL */}
                <div>
                  <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Endpoint</h3>
                  <div className="endpoint-badge">
                    <span className="method-tag">{getMethodAndUrl(activeSection).method}</span>
                    <code className="text-[13px] font-mono font-bold text-slate-300 select-all">
                      {getMethodAndUrl(activeSection).url}
                    </code>
                  </div>
                  {activeSection === 'tarot' && (
                    <p className="text-xs text-slate-400 mt-2 ml-1 font-medium">
                      Also available: <code className="text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded font-bold">/tarot-2</code> (2 cards) and <code className="text-indigo-400 bg-indigo-50 px-1.5 py-0.5 rounded font-bold">/tarot-3</code> (3 cards)
                    </p>
                  )}
                </div>

                {/* Request Headers */}
                <div>
                  <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Request Headers</h3>
                  <table className="param-table w-full">
                    <thead>
                      <tr>
                        <th>Header</th>
                        <th>Value</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getHeaders(activeSection).map((h, i) => (
                        <tr key={i}>
                          <td className="font-mono font-bold text-slate-700 text-[12px]">{h.name}</td>
                          <td className="font-mono text-indigo-600 text-[12px] font-semibold">{h.value}</td>
                          <td className="text-slate-500 text-[12px]">{h.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Body Parameters */}
                {getBodyParams(activeSection).length > 0 && (
                  <div>
                    <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-3">Body Parameters</h3>
                    <table className="param-table w-full">
                      <thead>
                        <tr>
                          <th>Parameter</th>
                          <th>Type</th>
                          <th>Required</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getBodyParams(activeSection).map((p, i) => (
                          <tr key={i}>
                            <td className="font-mono font-bold text-slate-700 text-[12px]">{p.name}</td>
                            <td>
                              <span className="inline-block bg-indigo-50 text-indigo-600 font-mono font-bold text-[11px] px-2 py-0.5 rounded-md">
                                {p.type}
                              </span>
                            </td>
                            <td>
                              {p.required ? (
                                <span className="inline-flex items-center text-[11px] font-bold text-red-500">
                                  <span className="required-dot" />
                                  Required
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-[11px] font-semibold text-slate-400">
                                  <span className="optional-dot" />
                                  Optional
                                </span>
                              )}
                            </td>
                            <td className="text-slate-500 text-[12px] leading-relaxed">{p.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {activeSection === 'fortune' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-2.5">
                      <span className="text-amber-500 text-lg">💡</span>
                      <div>
                        <p className="text-[12px] font-bold text-amber-800 mb-0.5">No Body Required</p>
                        <p className="text-[12px] text-amber-700">This endpoint does not require any request body parameters. Simply send the POST request with the required headers.</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'auth' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-2.5">
                      <span className="text-blue-500 text-lg">🔑</span>
                      <div>
                        <p className="text-[12px] font-bold text-blue-800 mb-0.5">API Key Authentication</p>
                        <p className="text-[12px] text-blue-700 leading-relaxed">All endpoints except login require an <code className="bg-blue-100 px-1 rounded text-[11px] font-bold">x-api-key</code> header. You can find your API key in the Account Dashboard under your active subscription's purchase code.</p>
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* Right: Code & Response (2 cols) */}
              <div className="lg:col-span-2 space-y-5 lg:sticky lg:top-[80px] h-fit">
                {/* Code Snippet */}
                <div className="code-block">
                  <div className="code-block-header">
                    <div className="flex gap-1">
                      {languages.map(lang => (
                        <button
                          key={lang.id}
                          onClick={() => setActiveLanguage(lang.id)}
                          className={`lang-tab ${activeLanguage === lang.id ? 'active' : ''}`}
                        >
                          {lang.label}
                        </button>
                      ))}
                    </div>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(getCodeSnippet(activeSection, activeLanguage))}
                    >
                      {copied ? (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          <span style={{ color: '#22c55e' }}>Copied</span>
                        </>
                      ) : (
                        <>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ maxHeight: '320px' }}>
                    <pre className="text-[12px] leading-relaxed font-mono" style={{ color: '#a5b4fc' }}>
                      <code>{getCodeSnippet(activeSection, activeLanguage)}</code>
                    </pre>
                  </div>
                </div>

                {/* Response Example */}
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="text-[11px] font-bold text-slate-400">Response</span>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-[11px] font-bold text-emerald-400 font-mono">200 OK</span>
                    </div>
                  </div>
                  <div className="p-4 overflow-x-auto" style={{ maxHeight: '300px' }}>
                    <pre className="text-[12px] leading-relaxed font-mono" style={{ color: '#6ee7b7' }}>
                      <code>{getResponseExample(activeSection)}</code>
                    </pre>
                  </div>
                </div>
              </div>

            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default ApiDocs;
